<template>
  <ChatwallInput :allowedImpersonators="allowedImpersonators" :currentUserAlias="userAlias" @update-post="updatePost" />
  <div class="chat__filter-menu" :class="{ 'chat__filter-menu--framed': disableinput }">
    <ul class="chat__filter-menu__list">
      <li class="filter-item" v-if="showFilterAll">
        <button class="btn btn__primary--outline chat-filter" :class="{ active: filter === 'all' }"
          @click="setPostFilter('all')">
          {{ filteralltext }}
        </button>
      </li>
      <li class="filter-item" v-if="showFilterUserNews">
        <button class="btn btn__primary--outline chat-filter" :class="{ active: filter === 'personal' }"
          @click="setPostFilter('personal')">
          {{ filterusernewstext }}
        </button>
      </li>
      <li class="filter-item" v-if="showFilterNews">
        <button class="btn btn__primary--outline chat-filter" :class="{ active: filter === 'news' }"
          @click="setPostFilter('news')">
          {{ filternewstext }}
        </button>
      </li>
    </ul>
  </div>
  <div class="chat__feed-container" ref="feed">
    <div class="chat__new-posts" v-if="newPostKeys.length">
      <button type="button" class="btn btn__primary" @click="showNewPosts">
        {{ newPostsLabel }} – vis
      </button>
    </div>
    <ChatwallContent :allowedImpersonators="allowedImpersonators" :posts="posts" :currentUserAlias="userAlias" :profileUrl="profileUrl"
      @update-post="updatePost" :doneLoading="doneLoading" />
    <div class="btn-container" v-if="loadMore">
      <button @click="getChatWallPosts(true)" class="btn btn__primary">Indlæs flere indlæg</button>
    </div>
  </div>
</template>
<script>

import ChatwallInput from "./components/Chatwall/ChatwallInputV2.vue";
import ChatwallContent from "./components/Chatwall/ChatwallContent.vue";
import { connectChatwallLive } from "./composables/chatwallLive.js";

// Opslag og nyheder har hvert sit id-rum.
const itemKey = (type, id) => `${type || "post"}-${id}`;

// En ny eller genpubliceret nyhed går til alle feeds; hvert feed tjekker selv om den er med (nyhedsmappe, tags
// og dato afgør det). Spredt over et par sekunder, så de åbne feeds ikke spørger serveren på samme tid — og
// tidligst efter to sekunder, så søgeindekset har nået at få den nye version.
const NEWS_CHECK_MIN_DELAY_MS = 2000;
const NEWS_CHECK_MAX_DELAY_MS = 15000;
const MAX_PAGE_SIZE = 100; // IntranetPostService.MaxPageSize

export default {
  name: "App-chatwall",
  components: { ChatwallContent, ChatwallInput },
  data() {
    return {
      imageAspectRatio: "",
      userAlias: "",
      userAreas: "",
      rootId: "",
      newsfolderid: "",
      posts: { posts: [] },
      filter: "all",
      isGroup: false,
      groupId: "",
      pinnedNews: "",
      newsTags: "",
      filternewstext: "Kommune",
      newsFeedByInterests: false,
      showFilterNews: false,
      filterusernewstext: "Mit",
      showFilterUserNews: false,
      filteralltext: "Alle",
      showFilterAll: true,
      page: 1,
      loadMore: false,
      allowedImpersonators: [],
      disableinput: false,
      profileUrl: "",
      doneLoading: false,
      // Live-feed (Teamwork 50431665): nye opslag fra andre, som endnu ikke er vist.
      newPostKeys: [],
      newsChecks: [],
      live: null,
    };
  },
  computed: {
    newPostsLabel() {
      const n = this.newPostKeys.length;
      const onlyNews = this.newPostKeys.every(k => k.startsWith("news-"));
      if (n === 1) return onlyNews ? "1 ny nyhed" : "1 nyt opslag";
      return onlyNews ? `${n} nye nyheder` : `${n} nye opslag`;
    },
  },
  methods: {
    feedRequest(page) {
      return {
        page,
        rootNode: this.rootId,
        pageSize: 10,
        userAlias: this.userAlias,
        imageAspectRatio: this.imageAspectRatio,
        filterTags: this.newsTags,
        newsFolderId: this.newsfolderid,
        newsFeedByInterests: this.newsFeedByInterests,
        pinnedNews: this.pinnedNews,
        filter: this.filter,
        isGroup: this.isGroup,
        areas: this.isGroup ? this.groupId : this.userAreas
      };
    },
    async fetchFeed(payload) {
      const response = await fetch(
        `${process.env.VUE_APP_API_URL}/api/MessageWall/GetPostsWithNews`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Fejl under hentning af posts");
      }
      return response.json();
    },
    async getChatWallPosts(loadMore = false) {
      this.doneLoading = false;
      try {
        if (loadMore) {
          this.page++;
        }
        const payload = this.feedRequest(this.page);
        console.log('Fetching posts with payload:', payload);
        const posts = await this.fetchFeed(payload);
        console.log('Received posts:', Array.isArray(posts?.posts) ? posts.posts.length : 'no posts array', posts);
        this.doneLoading = true;
        this.loadMore = posts.loadMore;
        if (loadMore) {
          // Siderne er offset-baserede: er der kommet nye opslag til siden sidst, ville "Indlæs flere"
          // ellers gentage nogle af dem.
          const shown = new Set(this.posts.posts.map(p => itemKey(p.postType, p.id)));
          this.posts.posts = [...this.posts.posts, ...posts.posts.filter(p => !shown.has(itemKey(p.postType, p.id)))];
        } else {
          this.posts = posts;
          this.page = 1;
        }
      } catch (error) {
        console.error("Der opstod en fejl:", error);
      }
    },
    async updatePost(newPost) {
      if (!this.posts.posts) {
        this.posts.posts = [];
      }

      if (newPost) {
        // Brugerens eget nye opslag skal ikke også tælle i "nye opslag"-bjælken.
        const key = itemKey(newPost.postType, newPost.id);
        this.newPostKeys = this.newPostKeys.filter(k => k !== key);
      }

      // Handle delete messages emitted from children
      if (newPost && (newPost._action === 'delete' || newPost.deleted === true)) {
        this.posts.posts = this.posts.posts.filter(p => !(p.id === newPost.id && (p.postType || 'post') === (newPost.postType || 'post')));
        this.posts = { ...this.posts };
        return;
      }

      // Upsert logic for adds/edits
      // Opslag og nyheder har hvert sit id-rum.
      const existingPostIndex = this.posts.posts.findIndex(post => post.id === newPost.id && (post.postType || 'post') === (newPost.postType || 'post'));
      if (existingPostIndex !== -1) {
        this.posts.posts.splice(existingPostIndex, 1, newPost);
      } else {
        this.posts.posts.unshift(newPost);
      }
      this.posts = { ...this.posts };
    },
    hasPost(type, id) {
      return (this.posts.posts || []).some(p => itemKey(p.postType, p.id) === itemKey(type, id));
    },
    onLiveMessage(msg) {
      const type = msg.itemType || "post";
      const key = itemKey(type, msg.postId);
      if (msg.kind === "created") {
        // Feedet skubbes ikke: brugeren vælger selv at hente de nye opslag.
        if (this.hasPost(type, msg.postId) || this.newPostKeys.includes(key)) return;
        if (type === "news") {
          if (this.showsNews()) this.verifyNews(msg.postId);
        } else if (this.filter !== "news") {
          this.newPostKeys = [...this.newPostKeys, key];
        }
      } else if (msg.kind === "updated" && type === "news") {
        // Genpubliceret: tags eller dato kan have flyttet nyheden ind i eller ud af feedet.
        if (this.showsNews()) this.verifyNews(msg.postId);
      } else if (msg.kind === "deleted") {
        this.newPostKeys = this.newPostKeys.filter(k => k !== key);
        if (this.hasPost(type, msg.postId)) {
          this.updatePost({ id: msg.postId, postType: type, _action: "delete" });
        }
      } else if (this.hasPost(type, msg.postId)) {
        this.refreshPost(type, msg.postId);
      }
    },
    showsNews() {
      // "Mine" viser kun opslag; grupperum viser ingen nyheder.
      return !this.isGroup && this.filter !== "personal";
    },
    verifyNews(id) {
      const filter = this.filter;
      const delay = NEWS_CHECK_MIN_DELAY_MS + Math.random() * (NEWS_CHECK_MAX_DELAY_MS - NEWS_CHECK_MIN_DELAY_MS);
      const timer = setTimeout(async () => {
        this.newsChecks = this.newsChecks.filter(t => t !== timer);
        try {
          // Så mange som feedet viser nu, så en nyhed længere nede ikke ser ud til at være faldet ud.
          const shown = (this.posts.posts || []).length;
          const request = { ...this.feedRequest(1), pageSize: Math.min(MAX_PAGE_SIZE, Math.max(10, shown)) };
          const feed = await this.fetchFeed(request);
          // Filteret kan være skiftet imens; så gælder svaret ikke længere.
          if (filter !== this.filter) return;

          const key = itemKey("news", id);
          const fresh = (feed.posts || []).find(p => itemKey(p.postType, p.id) === key);
          if (this.hasPost("news", id)) {
            if (fresh) {
              this.updatePost({ ...fresh, postType: fresh.postType || "news" });
            } else if (shown <= MAX_PAGE_SIZE) {
              this.updatePost({ id, postType: "news", _action: "delete" });
            }
          } else if (fresh && !this.newPostKeys.includes(key)) {
            this.newPostKeys = [...this.newPostKeys, key];
          } else if (!fresh) {
            this.newPostKeys = this.newPostKeys.filter(k => k !== key);
          }
        } catch (error) {
          console.error("Chatwall-live: kunne ikke tjekke nyheden", error);
        }
      }, delay);
      this.newsChecks = [...this.newsChecks, timer];
    },
    cancelNewsChecks() {
      this.newsChecks.forEach(clearTimeout);
      this.newsChecks = [];
    },
    async refreshPost(type, id) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/MessageWall/GetPostById`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ PostId: id, Type: type, ImageAspectRatio: this.imageAspectRatio }),
        });
        if (response.status === 404) {
          this.updatePost({ id, postType: type, _action: "delete" });
          return;
        }
        if (!response.ok) return;
        const post = await response.json();
        // Kun opdatering af et opslag der stadig vises (det kan være fjernet imens).
        if (post && this.hasPost(type, id)) {
          this.updatePost({ ...post, postType: post.postType || type });
        }
      } catch (error) {
        console.error("Chatwall-live: kunne ikke genindlæse opslaget", error);
      }
    },
    showNewPosts() {
      this.cancelNewsChecks();
      this.newPostKeys = [];
      this.page = 1;
      this.getChatWallPosts();
      if (this.$refs.feed && this.$refs.feed.scrollIntoView) {
        this.$refs.feed.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    onLiveReconnected() {
      // Forbindelsen har været væk, så signaler kan være tabt. Første side kan hentes igen uden at brugeren
      // mister noget; har brugeren indlæst flere sider, rører vi ikke listen.
      if (this.page === 1) {
        this.getChatWallPosts();
      }
    },
    setPostFilter(filter) {
      this.cancelNewsChecks();
      this.newPostKeys = [];
      this.filter = filter;
      this.page = 1;
      this.getChatWallPosts();
    },
    async getAllowedImpersonators() {
      try {
        fetch(
          `${process.env.VUE_APP_API_URL}/api/IntranetImpersonator/GetAvailableImpersonations?userAlias=${this.userAlias}`)
          .then((response) => {
            if (!response.ok) {
              throw new Error("Fejl under hentning af data");
            }
            return response.json();
          })
          .then((data) => {
            this.allowedImpersonators = data; // Opdater listen med brugere
          })
          .catch((error) => {
            console.error("Hente godkendte impersonaters fejlede!:", error);
          });
      }
      catch (error) {
        console.error(error);
      }
    },
  },
  mounted() {
    function findAncestorByClass(startEl, className) {
      let el = startEl;
      while (el && el !== document) {
        if (el.classList && el.classList.contains(className)) return el;
        el = el.parentElement || el.parentNode;
      }
      return null;
    }
    const container = document.getElementById('app-chatwall-viewer');
    const chatRoot = container ? findAncestorByClass(container, 'chat') : null;
    const chatwallWrapper = (chatRoot && chatRoot.querySelector('.chat-wrapper')) || document.querySelector('.chat-wrapper');
    console.log('Mounted ChatwallViewer.vue');
    this.imageAspectRatio = chatwallWrapper && chatwallWrapper.dataset.imageaspectratio;
    this.userAlias = chatwallWrapper && chatwallWrapper.dataset.useralias;
    this.rootId = chatwallWrapper && chatwallWrapper.dataset.rootid;
    this.newsfolderid = chatwallWrapper && chatwallWrapper.dataset.newsfolderid;
    this.isGroup = (chatwallWrapper && chatwallWrapper.dataset.isgroup) === "true";
    this.groupId = chatwallWrapper && chatwallWrapper.dataset.groupid;
    this.userAreas = chatwallWrapper && chatwallWrapper.dataset.userareas;
    this.showFilterNews = (chatwallWrapper && chatwallWrapper.dataset.filternews) === "true";
    this.filteralltext = chatwallWrapper && chatwallWrapper.dataset.filteralltext;
    if (this.filteralltext === "") {
      this.filteralltext = "Alle";
    }
    this.filternewstext = chatwallWrapper && chatwallWrapper.dataset.filternewstext;
    this.filterusernewstext = chatwallWrapper && chatwallWrapper.dataset.filterusernewstext;
    if(this.filternewstext === "") {
      this.filternewstext = "Kommune nyheder";
    }
    if(this.filterusernewstext === "") {
      this.filterusernewstext = "Mine nyheder";
    } 
    this.showFilterUserNews = (chatwallWrapper && chatwallWrapper.dataset.filterusernews) === "true";
    this.showFilterAll = (chatwallWrapper && chatwallWrapper.dataset.filterall) === "true";
    this.pinnedNews = chatwallWrapper && chatwallWrapper.dataset.pinnednews;
    this.newsTags = chatwallWrapper && chatwallWrapper.dataset.newstags;

    this.filter = this.showFilterAll ? "all" : this.showFilterUserNews ? "personal" : "news";
    this.disableinput = (chatwallWrapper && chatwallWrapper.dataset.disableinput) === 'true';
    this.profileUrl = chatwallWrapper && chatwallWrapper.dataset.profilurl;
    this.newsFeedByInterests = (chatwallWrapper && chatwallWrapper.dataset.newsfeedbyinterests) === "true";
    this.getChatWallPosts();
    this.getAllowedImpersonators();
    this.live = connectChatwallLive({
      groupId: this.isGroup ? this.groupId : null,
      onMessage: this.onLiveMessage,
      onReconnected: this.onLiveReconnected,
    });
  },
  beforeUnmount() {
    this.cancelNewsChecks();
    if (this.live) this.live.stop();
  }

};
</script>
<style scoped>
/* Følger med ned gennem feedet (sticky inden for .chat__feed-container). Wrapperen fanger ikke klik,
   så opslagene bag den kan stadig bruges; kun knappen gør. */
.chat__new-posts {
  position: sticky;
  top: 1rem;
  z-index: 20;
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  pointer-events: none;
  transition: top 0.2s ease;
}
.chat__new-posts .btn {
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
/* Sitets hover på btn__primary er en gennemsigtig baggrund; her ligger knappen oven på opslagene. */
.chat__new-posts .btn:hover,
.chat__new-posts .btn:focus {
  background: var(--color-white, #fff);
}
/* Headeren (headroom) kommer frem når man scroller op: læg bjælken under den. */
body:has(header.headroom--pinned:not(.headroom--top)) .chat__new-posts {
  top: calc(var(--header-height, 76px) + 1rem);
}
</style>
